import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";

/**
 * Player identity verification review (API i73). Photos are never listed with
 * a URL: `openDocument` mints a two-minute, audited link per click.
 */

export type IdentityFixKey =
  | "id_front" | "id_back" | "id_selfie" | "legal_name" | "birthdate" | "id_type"
  | "guardian_id" | "guardian_selfie" | "guardian_name";

export const IDENTITY_FIX_LABELS: Record<IdentityFixKey, string> = {
  id_front: "ID photo — front",
  id_back: "ID photo — back",
  id_selfie: "Selfie holding the ID",
  legal_name: "Legal name",
  birthdate: "Birthdate",
  id_type: "Which ID",
  guardian_id: "Guardian's ID",
  guardian_selfie: "Guardian's selfie with their ID",
  guardian_name: "Guardian's name",
};

export interface IdentityVerificationDto {
  id: string;
  user_id: string;
  status: string;
  requested_at: string | null;
  reviewed_at: string | null;
  reviewer_user_id: string | null;
  decision_reason: string | null;
  legal_name?: string | null;
  birthdate?: string | null;
  id_type: string | null;
  id_type_label: string | null;
  required_fixes: IdentityFixKey[];
  review_note: string | null;
  free_upgrade_granted: boolean;
  submitted_at: string | null;
  handle?: string | null;
  display_name?: string | null;
  avatar?: string | null;
  /** Ages 16–17 (i136). */
  guardian_name?: string | null;
  guardian_relationship?: string | null;
  guardian_relationship_label?: string | null;
  /** Detail only: the locked account birthday the ID must match (i135/i136). */
  account_birthdate?: string | null;
  age_band?: string;
  birthdate_matches?: boolean;
}

export interface BirthdayChangeDto {
  id: string;
  user_id: string;
  status: "pending" | "approved" | "denied";
  created_at: string;
  reviewed_at: string | null;
  handle?: string | null;
  display_name?: string | null;
  previous_birthdate?: string | null;
  requested_birthdate?: string;
  review_note?: string | null;
}

export interface IdentityDocumentDto {
  id: string | null;
  requirement_key: string;
  label: string;
  status: string;
  media_type?: string | null;
  size_bytes?: number | null;
  file_available: boolean;
  submitted_at?: string | null;
  decision_reason?: string | null;
}

export interface FreeAllowanceDto {
  limit: number | null;
  granted: number;
}

export interface IdentityDetailResponse {
  verification: IdentityVerificationDto;
  documents: IdentityDocumentDto[];
  history: Array<{ id: string; status: string; requested_at: string | null; reviewed_at: string | null; decision_reason: string | null }>;
  free_allowance: FreeAllowanceDto;
}

export interface IdentityAccessDto {
  url: string;
  expires_at: string;
  media_type: string | null;
  label: string;
}

const BASE = `${API_VERSION_PREFIX}/hq/identity-verifications`;
const BIRTHDAY = `${API_VERSION_PREFIX}/hq/birthday-changes`;

export function createIdentityVerificationApi(client: ApiClient) {
  return {
    listBirthdayChanges(scope: "awaiting" | "decided") {
      return client.get<{ requests: BirthdayChangeDto[] }>(`${BIRTHDAY}?scope=${scope}`);
    },
    birthdayChange(id: string) {
      return client.get<{ request: BirthdayChangeDto; documents: IdentityDocumentDto[] }>(`${BIRTHDAY}/${id}`);
    },
    openBirthdayDocument(id: string, documentId: string) {
      return client.post<{ access: IdentityAccessDto }>(`${BIRTHDAY}/${id}/documents/${documentId}/access`, {});
    },
    decideBirthdayChange(id: string, decision: "approve" | "deny", body: { note?: string }) {
      return client.post<{ request: { id: string; status: string } }>(
        `${BIRTHDAY}/${id}/actions/${decision}`,
        body,
        newIdempotencyKey(),
      );
    },
    list(scope: "awaiting" | "decided") {
      return client.get<{ verifications: IdentityVerificationDto[]; free_allowance: FreeAllowanceDto }>(
        `${BASE}?scope=${scope}`,
      );
    },
    detail(id: string) {
      return client.get<IdentityDetailResponse>(`${BASE}/${id}`);
    },
    openDocument(id: string, documentId: string) {
      return client.post<{ access: IdentityAccessDto }>(`${BASE}/${id}/documents/${documentId}/access`, {});
    },
    decide(
      id: string,
      decision: "accept" | "deny" | "request-modification",
      body: { note?: string; fixes?: IdentityFixKey[] },
    ) {
      return client.post<{ verification: { id: string; status: string; free_upgrade_granted: boolean } }>(
        `${BASE}/${id}/actions/${decision}`,
        body,
        newIdempotencyKey(),
      );
    },
  };
}

export type IdentityVerificationApi = ReturnType<typeof createIdentityVerificationApi>;
